import React, { useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import CircularProgress from '@mui/material/CircularProgress';
import ReadOnlyField from 'v3/Components/Form/ReadOnlyField';
import TextInput from 'v3/Components/Form/TextInput';
import Dropdown, { DropdownOption } from 'v3/Components/Form/Dropdown';
import {
  PrimaryButton,
  TertiaryButton,
  LinkButton,
} from 'v3/Components/Shared/Buttons';
import { ConfirmDialog } from 'v3/Components/Shared/ConfirmDialog';
import { useEnums, useToasts } from 'Context';
import { NamedCoreEnum } from 'Models';
import { buildEndpoint, useDataPoster, useHasPermissionsV3 } from 'Utilities';
import { ENDPOINTS } from 'Models/Templates/Routes/Routes';
import { PermissionCodesV3 } from 'Models/Authorization/permissionCodesV3';
import { ReviewerLifecycleStatus } from 'v3/Models/Enums';
import { ReviewerProfileModel } from './useReviewerProfileForm';
import { AxiosError } from 'axios';

export type ContactInformationData = {
  firstName?: string;
  lastName?: string;
  contactPreferenceValue?: number | null;
  contactPreferenceDisplayName?: string;
  workPhone?: string;
  cellPhone?: string;
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  additionalContactName?: string;
  additionalContactEmail?: string;
  additionalContactPhone?: string;
  email?: string;
};

const CONTACT_PREFERENCE_OPTIONS: DropdownOption[] = [
  { value: 1, displayName: 'Call' },
  { value: 2, displayName: 'Text' },
  { value: 3, displayName: 'Email' },
];

const EMPTY_OPTION: DropdownOption = { value: '', displayName: '' };

const toContactPrefOption = (value?: number | null): DropdownOption =>
  CONTACT_PREFERENCE_OPTIONS.find((o) => o.value === value) ?? EMPTY_OPTION;

const toStateOption = (options: DropdownOption[], value?: string): DropdownOption =>
  options.find((o) => o.value === value) ?? EMPTY_OPTION;

// Two-character codes of the 50 states (isUsState comes from the backend State enum), sorted.
// A previously saved value outside the 50 states (e.g. DC) is kept as an option so opening the
// card in edit mode doesn't silently drop it.
const buildStateOptions = (
  stateEnum: NamedCoreEnum,
  savedValue?: string | null
): DropdownOption[] =>
  Object.values(stateEnum)
    .filter((s) => s.isUsState || s.value === savedValue)
    .map((s) => String(s.value))
    .sort((a, b) => a.localeCompare(b))
    .map((code) => ({ value: code, displayName: code }));

const validationSchema = Yup.object({
  firstName: Yup.string().required('First Name is required'),
  lastName: Yup.string().required('Last Name is required'),
  email: Yup.string().required('Email is required').email('Email is not valid'),
  additionalContactEmail: Yup.string()
    .email('Additional contact email is not valid')
    .nullable(),
  zipCode: Yup.string()
    .matches(
      /^\d{5}(-\d{4})?$/,
      'Zip code must be in format 12345 or 12345-6789'
    )
    .nullable(),
});

type ReadOnlyProps = {
  data: ContactInformationData;
  reviewerId?: string;
  reviewerStatus?: number;
  onPasswordResetSuccess?: () => void;
};

// Reviewers still being onboarded have no portal account to reset a password for.
const PASSWORD_RESET_HIDDEN_STATUSES: number[] = [
  ReviewerLifecycleStatus.Onboarding,
  ReviewerLifecycleStatus.NotReady,
];

export const ContactInformationReadOnly: React.FC<ReadOnlyProps> = ({
  data,
  reviewerId,
  reviewerStatus,
  onPasswordResetSuccess,
}) => {
  const toast = useToasts();
  const hasPermissionsV3 = useHasPermissionsV3();
  const hasPasswordReset =
    hasPermissionsV3(PermissionCodesV3.Reviewer.Profile.PasswordReset) &&
    (reviewerStatus === undefined || !PASSWORD_RESET_HIDDEN_STATUSES.includes(reviewerStatus));
  const [resetting, setResetting] = useState(false);

  const [postReset] = useDataPoster(
    buildEndpoint(ENDPOINTS.reviewers.base, reviewerId, 'passwordReset'),
    undefined,
    false,
    '3.0'
  );

  const handlePasswordReset = async () => {
    setResetting(true);
    try {
      const resp: any = await postReset({});
      if (resp?.status === 200) {
        toast.success('Password reset email has been sent');
        onPasswordResetSuccess?.();
      } else {
        const message = (resp as AxiosError<any>)?.response?.data?.message;
        toast.error(
          message || 'Failed to send password reset. Please try again.'
        );
      }
    } catch {
      toast.error('Failed to send password reset. Please try again.');
    } finally {
      setResetting(false);
    }
  };

  return (
    <div className="contact-readonly">
      {/* Row 1: Reviewer Information | Address */}
      <div className="overview-columns">
        <div className="overview-columns__col">
          <div className="overview-field-group">
            <p className="overview-subsection-label">Reviewer Information</p>
            <ReadOnlyField forceLabel="First Name" value={data.firstName} />
            <ReadOnlyField forceLabel="Last Name" value={data.lastName} />
            <ReadOnlyField
              forceLabel="Contact Preference"
              value={data.contactPreferenceDisplayName}
            />
            <ReadOnlyField
              forceLabel="Work Phone Number"
              value={data.workPhone}
            />
            <ReadOnlyField
              forceLabel="Cell Phone Number"
              value={data.cellPhone}
            />
          </div>
        </div>
        <div className="overview-columns__col">
          <div className="overview-field-group">
            <p className="overview-subsection-label">Address</p>
            <ReadOnlyField
              forceLabel="Address Line 1"
              value={data.addressLine1}
            />
            <ReadOnlyField
              forceLabel="Address Line 2"
              value={data.addressLine2}
            />
            <ReadOnlyField forceLabel="City" value={data.city} />
            <ReadOnlyField forceLabel="State" value={data.state} />
            <ReadOnlyField forceLabel="Zip Code" value={data.zipCode} />
          </div>
        </div>
      </div>
      {/* Row 2: Additional Point of Contact | Account Details */}
      <div className="overview-columns">
        <div className="overview-columns__col">
          <div className="overview-field-group">
            <p className="overview-subsection-label">
              Additional Point of Contact
            </p>
            <ReadOnlyField
              forceLabel="Name"
              value={data.additionalContactName}
            />
            <ReadOnlyField
              forceLabel="Email"
              value={data.additionalContactEmail}
            />
            <ReadOnlyField
              forceLabel="Phone Number"
              value={data.additionalContactPhone}
            />
          </div>
        </div>
        <div className="overview-columns__col">
          <div className="overview-field-group">
            <p className="overview-subsection-label">Account Details</p>
            <ReadOnlyField forceLabel="Email" value={data.email} />
            {hasPasswordReset && data.email && (
              <LinkButton onClick={handlePasswordReset} disabled={resetting}>
                {resetting ? (
                  <CircularProgress size={12} style={{ color: 'inherit' }} />
                ) : (
                  'Request Password Reset'
                )}
              </LinkButton>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

type ActiveProps = {
  data: ContactInformationData;
  reviewerId?: string;
  reviewer?: ReviewerProfileModel;
  onCancel?: () => void;
  onSaveSuccess?: (updated: ContactInformationData) => void;
};

type FormValues = {
  firstName: string;
  lastName: string;
  contactPreference: DropdownOption;
  workPhone: string;
  cellPhone: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: DropdownOption;
  zipCode: string;
  additionalContactName: string;
  additionalContactEmail: string;
  additionalContactPhone: string;
  email: string;
};

export const ContactInformationActive: React.FC<ActiveProps> = ({
  data,
  reviewerId,
  reviewer,
  onCancel,
  onSaveSuccess,
}) => {
  const toast = useToasts();
  const { state: stateEnum } = useEnums();
  const stateOptions = React.useMemo(
    () => buildStateOptions(stateEnum, data.state),
    [stateEnum, data.state]
  );
  const [saving, setSaving] = useState(false);
  const [discardOpen, setDiscardOpen] = useState(false);
  const isEmailLocked = reviewer?.status?.value === ReviewerLifecycleStatus.Active;

  const [postData] = useDataPoster(
    buildEndpoint(ENDPOINTS.reviewers.base, reviewerId, 'contactInformation'),
    undefined,
    true,
    '3.0'
  );

  const formik = useFormik<FormValues>({
    initialValues: {
      firstName: data.firstName ?? '',
      lastName: data.lastName ?? '',
      contactPreference: toContactPrefOption(data.contactPreferenceValue),
      workPhone: data.workPhone ?? '',
      cellPhone: data.cellPhone ?? '',
      addressLine1: data.addressLine1 ?? '',
      addressLine2: data.addressLine2 ?? '',
      city: data.city ?? '',
      state: toStateOption(stateOptions, data.state),
      zipCode: data.zipCode ?? '',
      additionalContactName: data.additionalContactName ?? '',
      additionalContactEmail: data.additionalContactEmail ?? '',
      additionalContactPhone: data.additionalContactPhone ?? '',
      email: data.email ?? '',
    },
    validationSchema,
    validateOnChange: false,
    validateOnBlur: true,
    onSubmit: () => {},
  });

  const handleSave = async () => {
    const errors = await formik.validateForm();
    if (Object.keys(errors).length > 0) {
      const touched = Object.keys(errors).reduce<Record<string, boolean>>(
        (acc, k) => {
          acc[k] = true;
          return acc;
        },
        {}
      );
      formik.setTouched(touched);
      toast.error('Please correct the highlighted fields before saving');
      return;
    }

    setSaving(true);
    const { values } = formik;
    const payload = {
      firstName: values.firstName,
      lastName: values.lastName,
      contactPreferenceValue:
        values.contactPreference.value !== ''
          ? Number(values.contactPreference.value)
          : null,
      workPhone: values.workPhone || null,
      cellPhone: values.cellPhone || null,
      addressLine1: values.addressLine1 || null,
      addressLine2: values.addressLine2 || null,
      city: values.city || null,
      state: values.state.value !== '' ? String(values.state.value) : null,
      zipCode: values.zipCode || null,
      additionalContactName: values.additionalContactName || null,
      additionalContactEmail: values.additionalContactEmail || null,
      additionalContactPhone: values.additionalContactPhone || null,
      email: values.email,
    };

    try {
      const resp: any = await postData(payload);
      if (resp?.status === 200) {
        toast.success('Contact information has been updated');
        onSaveSuccess?.({
          ...payload,
          contactPreferenceDisplayName: values.contactPreference.displayName || undefined,
        } as ContactInformationData);
      } else if ((resp as AxiosError)?.response?.status === 400) {
        const respData = (resp as AxiosError<any>)?.response?.data;
        if (respData?.errors) {
          const beErrors: Record<string, string> = {};
          Object.entries(respData.errors).forEach(([key, value]) => {
            beErrors[key.charAt(0).toLowerCase() + key.slice(1)] = (
              value as string[]
            )[0];
          });
          formik.setErrors(beErrors);
        }
        toast.error('Please correct the highlighted fields before saving');
      } else {
        toast.error('An error occurred. Please try again.');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleCancelClick = () => {
    if (formik.dirty) {
      setDiscardOpen(true);
    } else {
      onCancel?.();
    }
  };

  return (
    <div className="contact-active-form">
      {/* Row 1: Reviewer Information | Address */}
      <div className="overview-columns">
        <div className="overview-columns__col">
          <p className="overview-subsection-label">Reviewer Information</p>
          <TextInput
            label="First Name"
            required
            value={formik.values.firstName}
            onChange={(e) => formik.setFieldValue('firstName', e.target.value)}
            error={
              formik.touched.firstName ? formik.errors.firstName : undefined
            }
          />
          <TextInput
            label="Last Name"
            required
            value={formik.values.lastName}
            onChange={(e) => formik.setFieldValue('lastName', e.target.value)}
            error={formik.touched.lastName ? formik.errors.lastName : undefined}
          />
          <Dropdown
            label="Contact Preference"
            options={CONTACT_PREFERENCE_OPTIONS}
            value={formik.values.contactPreference}
            onChange={(v) => formik.setFieldValue('contactPreference', v)}
            showIcons={false}
          />
          <TextInput
            label="Work Phone Number"
            value={formik.values.workPhone}
            onChange={(e) => formik.setFieldValue('workPhone', e.target.value)}
            placeholder="(713)-932-7544 ext 103"
          />
          <TextInput
            label="Cell Phone Number"
            value={formik.values.cellPhone}
            onChange={(e) => formik.setFieldValue('cellPhone', e.target.value)}
            placeholder="(713)-932-7544 ext 103"
          />
        </div>

        <div className="overview-columns__col">
          <p className="overview-subsection-label">Address</p>
          <TextInput
            label="Address Line 1"
            value={formik.values.addressLine1}
            onChange={(e) =>
              formik.setFieldValue('addressLine1', e.target.value)
            }
          />
          <TextInput
            label="Address Line 2"
            value={formik.values.addressLine2}
            onChange={(e) =>
              formik.setFieldValue('addressLine2', e.target.value)
            }
          />
          <TextInput
            label="City"
            value={formik.values.city}
            onChange={(e) => formik.setFieldValue('city', e.target.value)}
          />
          <Dropdown
            label="State"
            options={stateOptions}
            value={formik.values.state}
            onChange={(v) => formik.setFieldValue('state', v)}
            showIcons={false}
            sortAlphabetically={false}
          />
          <TextInput
            label="Zip Code"
            value={formik.values.zipCode}
            onChange={(e) => formik.setFieldValue('zipCode', e.target.value)}
            error={formik.touched.zipCode ? formik.errors.zipCode : undefined}
            placeholder="77055 or 77055-1234"
            maxLength={10}
          />
        </div>
      </div>

      {/* Row 2: Additional Point of Contact | Account Details */}
      <div className="overview-columns">
        <div className="overview-columns__col">
          <p className="overview-subsection-label">
            Additional Point of Contact
          </p>
          <TextInput
            label="Name"
            value={formik.values.additionalContactName}
            onChange={(e) =>
              formik.setFieldValue('additionalContactName', e.target.value)
            }
          />
          <TextInput
            label="Email"
            value={formik.values.additionalContactEmail}
            onChange={(e) =>
              formik.setFieldValue('additionalContactEmail', e.target.value)
            }
            error={
              formik.touched.additionalContactEmail
                ? formik.errors.additionalContactEmail
                : undefined
            }
            placeholder="abc@example.com"
          />
          <TextInput
            label="Phone Number"
            value={formik.values.additionalContactPhone}
            onChange={(e) =>
              formik.setFieldValue('additionalContactPhone', e.target.value)
            }
            placeholder="(713)-932-7544 ext 103"
          />
        </div>

        <div className="overview-columns__col">
          <p className="overview-subsection-label">Account Details</p>
          <TextInput
            label="Email"
            required
            value={formik.values.email}
            onChange={(e) => formik.setFieldValue('email', e.target.value)}
            error={formik.touched.email ? formik.errors.email : undefined}
            placeholder="abc@example.com"
            disabled={isEmailLocked}
          />
        </div>
      </div>

      <div className="license-active__footer">
        <TertiaryButton
          title="Cancel"
          className="centered"
          style={{ padding: '0 24px' }}
          onClick={handleCancelClick}
        />
        <PrimaryButton
          title="Save"
          className="centered"
          style={{ width: 'auto', padding: '0 20px' }}
          onClick={handleSave}
          disabled={saving}
        >
          {saving && (
            <CircularProgress
              size={16}
              thickness={5}
              style={{ color: 'inherit' }}
            />
          )}
        </PrimaryButton>
      </div>

      <ConfirmDialog
        dialogVisible={discardOpen}
        toggleDialog={() => setDiscardOpen(false)}
        title="Discard Changes"
        primaryButtonText="Discard"
        isPrimaryDanger
        OnPrimaryButtonClick={() => {
          setDiscardOpen(false);
          onCancel?.();
        }}
        secondaryButtonText="Keep Editing"
        OnSecondaryButtonClick={() => setDiscardOpen(false)}
      >
        <p style={{ padding: '16px 24px' }}>
          Are you sure you want to discard your changes? Any unsaved data will
          be lost.
        </p>
      </ConfirmDialog>
    </div>
  );
};
